import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import '../styles/brand.css';

const CohortManagement = () => {
  const { currentUser, userProfile } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCohort, setEditingCohort] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    programId: '',
    startDate: '',
    endDate: '',
    capacity: 30,
    status: 'active'
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load cohorts
      const cohortsCollection = collection(db, 'cohorts');
      const cohortsSnapshot = await getDocs(cohortsCollection);
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now()),
        startDate: doc.data().startDate?.toDate?.() || new Date(doc.data().startDate || Date.now()),
        endDate: doc.data().endDate?.toDate?.() || new Date(doc.data().endDate || Date.now())
      }));
      
      // Load programs
      const programsCollection = collection(db, 'programs');
      const programsSnapshot = await getDocs(programsCollection);
      const programsData = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load users to count enrollments
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersData = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(isRealUser);
      
      setCohorts(cohortsData);
      setPrograms(programsData);
      setUsers(usersData);
      
    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading cohort data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleShowModal = (cohort = null) => {
    if (cohort) {
      setEditingCohort(cohort);
      setFormData({
        name: cohort.name,
        description: cohort.description || '',
        programId: cohort.programId || '',
        startDate: cohort.startDate ? cohort.startDate.toISOString().split('T')[0] : '',
        endDate: cohort.endDate ? cohort.endDate.toISOString().split('T')[0] : '',
        capacity: cohort.capacity || 30,
        status: cohort.status || 'active'
      });
    } else {
      setEditingCohort(null);
      setFormData({
        name: '',
        description: '',
        programId: '',
        startDate: '',
        endDate: '',
        capacity: 30,
        status: 'active'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCohort(null);
    setValidated(false);
    setSubmitting(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);
    if (!e.target.checkValidity()) {
      e.stopPropagation();
      return;
    }
    setSubmitting(true);
    try {
      const cohortData = {
        ...formData,
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        capacity: parseInt(formData.capacity),
        updatedAt: serverTimestamp()
      };

      if (editingCohort) {
        // Update existing cohort
        await updateDoc(doc(db, 'cohorts', editingCohort.id), cohortData);
        showAlert('Cohort updated successfully!', 'success');
      } else {
        // Create new cohort
        await addDoc(collection(db, 'cohorts'), {
          ...cohortData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid
        });
        showAlert('Cohort created successfully!', 'success');
      }
      
      handleCloseModal();
      loadData();
      
    } catch (error) {
      devError('Error saving cohort:', error);
      showAlert('Error saving cohort', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (cohortId) => {
    if (window.confirm('Are you sure you want to delete this cohort? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'cohorts', cohortId));
        showAlert('Cohort deleted successfully!', 'success');
        loadData();
      } catch (error) {
        devError('Error deleting cohort:', error);
        showAlert('Error deleting cohort', 'danger');
      }
    }
  };

  const isActiveLearner = (user) => {
    if (!user || user.role !== 'learner') return false;
    if (user.deleted || user.deactivated) return false;
    if (user.isActive === false) return false;
    const status = (user.status || '').toLowerCase();
    if (['inactive', 'dropoff', 'dropped'].includes(status)) return false;
    return true;
  };

  const getEnrollmentCount = (cohortId) => {
    return users.filter(user => user.cohortId === cohortId && isActiveLearner(user)).length;
  };

  const getInactiveCount = (cohortId) => {
    return users.filter(user => user.cohortId === cohortId && !isActiveLearner(user)).length;
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.name : 'No Program';
  };

  const getStatusBadge = (status) => {
    const variants = {
      'active': 'success',
      'inactive': 'secondary',
      'completed': 'primary',
      'cancelled': 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access cohort management.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="cohorts" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading cohorts...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="cohorts" />
      
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Cohort Management</h1>
              <p className="mission-text mb-4">
                Organize and manage learner cohorts
              </p>
              <p className="mb-0">
                Create and manage learning cohorts to group students effectively, track their progress, and facilitate collaborative learning experiences within structured educational programs.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="outline-light" onClick={() => handleShowModal()}>
                ➕ Add New Cohort
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        {/* Cohorts Overview */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{cohorts.length}</h3>
                <small className="text-muted">Total Cohorts</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{cohorts.filter(c => c.status === 'active').length}</h3>
                <small className="text-muted">Active Cohorts</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{users.filter(u => u.cohortId && isActiveLearner(u)).length}</h3>
                <small className="text-muted">Total Enrollments</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">
                  {Math.round(cohorts.reduce((sum, cohort) => {
                    const enrolled = getEnrollmentCount(cohort.id);
                    return sum + (enrolled / cohort.capacity * 100);
                  }, 0) / cohorts.length) || 0}%
                </h3>
                <small className="text-muted">Avg Capacity</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Cohorts Table */}
        <Card>
          <Card.Header>
            <h5>All Cohorts</h5>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Program</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Enrollments</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.map((cohort) => {
                  const enrollmentCount = getEnrollmentCount(cohort.id);
                  const inactiveCount = getInactiveCount(cohort.id);
                  return (
                    <tr key={cohort.id}>
                      <td>
                        <strong>{cohort.name}</strong>
                        {cohort.description && (
                          <div><small className="text-muted">{cohort.description}</small></div>
                        )}
                      </td>
                      <td>{getProgramName(cohort.programId)}</td>
                      <td>{cohort.startDate.toLocaleDateString()}</td>
                      <td>{cohort.endDate.toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Badge bg="info">{enrollmentCount}</Badge>
                          <Badge bg="secondary">{inactiveCount} inactive</Badge>
                        </div>
                      </td>
                      <td>
                        <span className={enrollmentCount >= cohort.capacity ? 'text-danger' : 'text-success'}>
                          {enrollmentCount}/{cohort.capacity}
                        </span>
                      </td>
                      <td>{getStatusBadge(cohort.status)}</td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="me-1"
                          onClick={() => handleShowModal(cohort)}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDelete(cohort.id)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {cohorts.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      No cohorts found. Create your first cohort to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Add/Edit Cohort Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCohort ? 'Edit Cohort' : 'Add New Cohort'}
            </Modal.Title>
          </Modal.Header>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cohort Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Cohort 2024-A"
                    />
                    <Form.Control.Feedback type="invalid">Cohort name is required.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Program</Form.Label>
                    <Form.Select
                      name="programId"
                      value={formData.programId}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Program</option>
                      {programs.map(program => (
                        <option key={program.id} value={program.id}>
                          {program.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the cohort"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">Start date is required.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      required
                    />
                    <Form.Control.Feedback type="invalid">End date is required.</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Capacity *</Form.Label>
                    <Form.Control
                      type="number"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      required
                      min="1"
                      max="500"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status *</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" />{editingCohort ? 'Updating...' : 'Creating...'}</>
                ) : (
                  editingCohort ? 'Update Cohort' : 'Create Cohort'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default CohortManagement;
